import { AsyncSelect } from "components/AsyncInput/AsyncSelect";
import {
  showErrorToast,
  showSuccessToast,
} from "components/CustomAlerts/CustomToast";
import { Input } from "components/Input/Input";
import { Select } from "components/Select/Select";
import { useFormik } from "formik";
import { CreateCajaCurricularPciI } from "interfaces/CreateCajaCurricularPci";
import { CreateEspacioCurricularPciI } from "interfaces/CreateEspacioCurricularPci";
import { EspacioCurricularI } from "interfaces/EspacioCurricular";
import { PlanEstudioI } from "interfaces/PlanEstudio";
import { NormativaI } from "interfaces/Normativa";
import { useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Collapse, Modal, Row, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { PRIVATE_ROUTE } from "routes/privateRoutes";
import { createPciSchema } from "schemas/pci/createPciSchema";
import { CajaCurricularPciABM } from "../CajaCurricularPciABM/CajaCurricularPciABM";
import { EspacioCurricularPciABM } from "../EspacioCurricularPciABM/EspacioCurricularPciABM";
import { getInstituciones } from "services/institucion";
import { getPlanesEstudio } from "services/planesEstudio";
import { getPciById, postPci, putPci } from "services/pci";
import { getEspaciosCurricularesByCajaCurricular } from "services/espacioCurricular";
import { getNormativas } from "services/normativas";
import { fetchEstados } from "store/slices/appSlice";
import { AppDispatch, RootState } from "store/store";
import { CloseIcon } from "assets/icons/CloseIcon";
import { EditIcon } from "assets/icons/EditIcon";
import { LISTA_COLORES_ICONOS } from "assets/icons/LISTA_COLORES_ICONOS";
import { EspaciosTable, EspaciosTableRow } from "components/EspaciosTable/EspaciosTable";
import { EspaciosComparador, ComparadorEspacioBase, ComparadorEspacioPci } from "components/EspaciosComparador/EspaciosComparador";
import "components/AsyncInput/components/selectedElement.css";


const AUTO_FILL_ESPACIOS_ON_CAJA_CREATION = true;

export type abmActionI = "create" | "edit";

interface Props {
  abmAction?: abmActionI;
}

interface pciABMValuesI {
  id?: string | number;
  nombre: string;
  anio_implementacion: number | string;
  plan_estudio_id: number | string;
  normativas_ids: (number | string)[];
  institucion_id: number | string;
  estado_id: number | string;
}

interface defaultOptionsSelectedI {
  default_selected_plan_estudio: PlanEstudioI | null;
  default_selected_normativas: NormativaI[] | null;
  default_selected_institucion: any | null;
}

export const PCIABM = ({ abmAction = "create" }: Props) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const { estados, loadingAppData } = useSelector(
    (st: RootState) => st.appData,
  );

  const [modalsActionType, setModalsActionType] = useState<
    "create" | "edit" | undefined
  >();
  const [showModalABM, setShowModalABM] = useState({
    cajaCurricularPci: false,
    espacioCurricularPci: false,
  });

  const [instituciones, setInstituciones] = useState<any[]>([]);
  const [isLoadingInstituciones, setIsLoadingInstituciones] = useState(false);

  const [isLoading, setIsLoading] = useState({
    form: false,
  });

  const [defaultOptionsSelected, setDefaultOptionsSelected] =
    useState<defaultOptionsSelectedI>({
      default_selected_plan_estudio: null,
      default_selected_normativas: null,
      default_selected_institucion: null,
    });

  // --- Estado local para sub-entidades inline ---
  const [cajaCurricularPciList, setCajaCurricularPciList] = useState<
    CreateCajaCurricularPciI[]
  >([]);

  // --- Estado para edición de sub-entidades ---
  const [editingCajaIndex, setEditingCajaIndex] = useState<number | null>(null);
  const [editingEspacioIndex, setEditingEspacioIndex] = useState<{
    cajaIndex: number;
    espacioIndex: number;
  } | null>(null);

  // --- Estado para confirmación de eliminación de espacio ---
  const [pendingDeleteEspacio, setPendingDeleteEspacio] = useState<{
    cajaIndex: number;
    espacioIndex: number;
  } | null>(null);

  // --- Estado para comparador base vs PCI ---
  const [showComparador, setShowComparador] = useState(false);
  const [espaciosBaseComparador, setEspaciosBaseComparador] = useState<EspacioCurricularI[]>([]);
  const [loadingComparador, setLoadingComparador] = useState(false);

  const defaultInitialValues: pciABMValuesI = {
    nombre: "",
    anio_implementacion: "",
    plan_estudio_id: "",
    normativas_ids: [],
    institucion_id: "",
    estado_id: "",
  };

  const handleReturn = () => {
    navigate(PRIVATE_ROUTE.PCI);
  };

  const handleSubmitCreate = async (params: any) => {
    try {
      const payload = {
        ...params,
        cajaCurricularPci: cajaCurricularPciList.length > 0 ? cajaCurricularPciList[0] : null,
      };
      // Eliminar el nombre del area y el nombre del tipo espacio de los espacios pci
      payload.cajaCurricularPci.espaciosCurricularesPci.forEach((espacio: any) => {
        delete espacio.area_nombre;
        delete espacio.tipo_espacio_nombre;
      });
      console.log(payload);
      
      // return
      const newPci = await postPci({ params: payload });
      handleReturn();
      showSuccessToast("¡El PCI fue creado con éxito!");
    } catch (err: any) {
      showErrorToast("El PCI no pudo ser creado.");
      console.log(err.message);
    }
  };

  const handleSubmitEdit = async (params: any) => {
    try {
      const payload = {
        ...params,
        cajaCurricularPci: cajaCurricularPciList.length > 0 ? cajaCurricularPciList[0] : null,
      };
      payload.cajaCurricularPci.espaciosCurricularesPci.forEach((espacio: any) => {
        delete espacio.area_nombre;
        delete espacio.tipo_espacio_nombre;
      });
      const updatedPci = await putPci({ params: payload });
      handleReturn();
      showSuccessToast("¡El PCI fue editado con éxito!");
    } catch (err: any) {
      showErrorToast("El PCI no pudo ser editado.");
      console.log(err.message);
    }
  };

  const {
    values,
    errors,
    touched,
    isSubmitting,
    handleBlur,
    handleChange,
    handleSubmit,
    setValues,
  } = useFormik({
    initialValues: defaultInitialValues,
    validationSchema: createPciSchema,
    onSubmit: abmAction == "create" ? handleSubmitCreate : handleSubmitEdit,
  });

  const handleCreateModal = (modalName: string) => {
    setModalsActionType("create");
    setEditingCajaIndex(null);
    setEditingEspacioIndex(null);
    setShowModalABM((st) => ({ ...st, [modalName]: true }));
  };

  const handleCloseModal = (modalName: string) => {
    setShowModalABM((st) => ({ ...st, [modalName]: false }));
    setTimeout(() => {
      setModalsActionType(undefined);
      setEditingCajaIndex(null);
      setEditingEspacioIndex(null);
    }, 500);
  };

  // --- Callbacks para Caja Curricular ---
  const handleAddCajaCurricular = (newCaja: CreateCajaCurricularPciI) => {
    setCajaCurricularPciList((prev) => [...prev, newCaja]);
  };

  const handleEditCajaCurricular = (
    updatedCaja: CreateCajaCurricularPciI,
  ) => {
    if (editingCajaIndex === null) return;
    setCajaCurricularPciList((prev) => {
      const updated = [...prev];
      updated[editingCajaIndex] = updatedCaja;
      return updated;
    });
  };

  const handleRemoveCajaCurricular = (index: number) => {
    setCajaCurricularPciList((prev) => prev.filter((_, i) => i !== index));
  };

  const handleOpenEditCaja = (cajaIndex: number) => {
    setEditingCajaIndex(cajaIndex);
    setModalsActionType("edit");
    setShowModalABM((st) => ({ ...st, cajaCurricularPci: true }));
  };

  // --- Callbacks para Espacio Curricular ---
  const handleAddEspacioCurricular = (
    newEspacio: CreateEspacioCurricularPciI,
    cajaIndex: number,
  ) => {
    setCajaCurricularPciList((prev) => {
      if (cajaIndex < 0 || cajaIndex >= prev.length) return prev;
      const updated = [...prev];
      updated[cajaIndex] = {
        ...updated[cajaIndex],
        espaciosCurricularesPci: [
          ...updated[cajaIndex].espaciosCurricularesPci,
          newEspacio,
        ],
      };
      return updated;
    });
  };

  const handleEditEspacioCurricular = (
    updatedEspacio: CreateEspacioCurricularPciI,
    cajaIndex: number,
  ) => {
    if (editingEspacioIndex === null) return;
    setCajaCurricularPciList((prev) => {
      const updated = [...prev];
      const espacios = [...updated[editingEspacioIndex.cajaIndex].espaciosCurricularesPci];
      espacios[editingEspacioIndex.espacioIndex] = updatedEspacio;
      updated[editingEspacioIndex.cajaIndex] = {
        ...updated[editingEspacioIndex.cajaIndex],
        espaciosCurricularesPci: espacios,
      };
      return updated;
    });
  };

  const handleRemoveEspacioCurricular = (
    cajaIndex: number,
    espacioIndex: number,
  ) => {
    setCajaCurricularPciList((prev) => {
      const updated = [...prev];
      updated[cajaIndex] = {
        ...updated[cajaIndex],
        espaciosCurricularesPci: updated[
          cajaIndex
        ].espaciosCurricularesPci.filter((_, i) => i !== espacioIndex),
      };
      return updated;
    });
  };

  // --- Confirmación de eliminación de espacio ---
  const handleRequestDeleteEspacio = (cajaIndex: number, espacioIndex: number) => {
    setPendingDeleteEspacio({ cajaIndex, espacioIndex });
  };

  const handleConfirmDeleteEspacio = () => {
    if (pendingDeleteEspacio) {
      handleRemoveEspacioCurricular(
        pendingDeleteEspacio.cajaIndex,
        pendingDeleteEspacio.espacioIndex,
      );
      setPendingDeleteEspacio(null);
    }
  };

  const handleCancelDeleteEspacio = () => {
    setPendingDeleteEspacio(null);
  };

  const handleOpenEditEspacio = (cajaIndex: number, espacioIndex: number) => {
    setEditingEspacioIndex({ cajaIndex, espacioIndex });
    setModalsActionType("edit");
    setShowModalABM((st) => ({ ...st, espacioCurricularPci: true }));
  };

  // --- Manejo unificado de onSuccess de modales ---
  const handleCajaSuccess = async (caja: CreateCajaCurricularPciI) => {
    if (modalsActionType === "edit") {
      handleEditCajaCurricular(caja);
    } else {
      // Si el auto-fill está activado y se está creando una nueva caja,
      // buscar los espacios base de la caja curricular y agregarlos automáticamente
      if (
        AUTO_FILL_ESPACIOS_ON_CAJA_CREATION &&
        caja.caja_curricular_id
      ) {
        try {
          const res = await getEspaciosCurricularesByCajaCurricular({
            caja_curricular_id: caja.caja_curricular_id,
          });
          
          
          const espaciosBase = res?.elementos ?? [];
          const espaciosAutoFill: CreateEspacioCurricularPciI[] =
            espaciosBase.map((esp: any) => ({
              espacio_curricular_id: esp.id,
              nombre: esp.nombre ?? "",
              carga_horaria: esp.horas_semanales ?? esp.carga_horaria ?? "",
              cuatrimestre: esp.cuatrimestre ?? null,
              detalle_modificacion: "Sin modificación",
              anio: esp.anio ?? "",
              area_id: esp.area?.id ?? "",
              area_nombre: esp.area?.nombre ?? "",
              tipo_espacio_id:
                esp.tipo_espacio?.id ?? esp.tipoEspacio?.id ?? "",
              tipo_espacio_nombre:
                esp.tipo_espacio?.nombre ?? esp.tipoEspacio?.nombre ?? "",
            }));
          
          const cajaConEspacios: CreateCajaCurricularPciI = {
            ...caja,
            espaciosCurricularesPci: espaciosAutoFill,
          };
          handleAddCajaCurricular(cajaConEspacios);
        } catch (err) {
          // Si falla el auto-fill, agregar la caja sin espacios
          handleAddCajaCurricular(caja);
        }
      } else {
        handleAddCajaCurricular(caja);
      }
    }
  };

  const handleEspacioSuccess = (
    espacio: CreateEspacioCurricularPciI,
    cajaIndex: number,
  ) => {
    if (modalsActionType === "edit") {
      handleEditEspacioCurricular(espacio, cajaIndex);
    } else {
      handleAddEspacioCurricular(espacio, cajaIndex);
    }
  };

  const loadInstituciones = async () => {
    try {
      setIsLoadingInstituciones(true);
      const response = await getInstituciones();
      setInstituciones(response?.instituciones ?? []);
    } catch (err) {
      console.log("Error cargando instituciones", err);
    } finally {
      setIsLoadingInstituciones(false);
    }
  };

  const loadEditForm = async () => {
    const errorLoadingResolve = () => {
      showErrorToast("No fue posible cargar el PCI a editar");
      navigate(PRIVATE_ROUTE.PCI);
    };

    try {
      setIsLoading((st) => ({ ...st, form: true }));

      if (!id) return errorLoadingResolve();

      const pci = await getPciById(id);

      if (!pci) return errorLoadingResolve();


      const initialValues = {
        id: pci.id,
        nombre: pci.nombre,
        anio_implementacion: pci.anio_implementacion,
        plan_estudio_id: pci.planEstudio?.id,
        normativas_ids: pci.normativas?.map((n: NormativaI) => n.id) ?? [],
        institucion_id: pci.institucion?.id,
        estado_id: pci.estado?.id,
      };

      setDefaultOptionsSelected({
        default_selected_plan_estudio: pci.planEstudio,
        default_selected_normativas: pci.normativas ?? null,
        default_selected_institucion: pci.institucion ?? null,
      });

      // Cargar la caja curricular con sus espacios en el estado local
      
      if (pci.cajaCurricularPci) {
        const caja = pci.cajaCurricularPci;
        const cajaParaEstado: CreateCajaCurricularPciI = {
          id: caja.id,
          caja_curricular_id: caja.cajaCurricularBase?.id ?? caja.caja_curricular_id,
          descripcion: caja.descripcion,
          version: caja.version,
          espaciosCurricularesPci: caja.espaciosCurricularesPci
            ? caja.espaciosCurricularesPci.map((esp: any) => ({
                id: esp.id,
                nombre: esp.nombre,
                carga_horaria: esp.carga_horaria,
                cuatrimestre: esp.cuatrimestre ?? null,
                detalle_modificacion: esp.detalle_modificacion,
                anio: esp.anio ?? "",
                area_id: esp.area?.id ?? "",
                area_nombre: esp.area?.nombre ?? "",
                tipo_espacio_id: esp.tipoEspacio?.id ?? "",
                tipo_espacio_nombre: esp.tipoEspacio?.nombre ?? "",
                espacio_curricular_id: esp.id_espacio_base ?? esp.espacio_curricular_id,
              }))
            : [],
        };
        
        setCajaCurricularPciList([cajaParaEstado]);
      }

      setValues(initialValues);
    } catch (err) {
      errorLoadingResolve();
    } finally {
      setIsLoading((st) => ({ ...st, form: false }));
    }
  };

  const addNewPlanEstudio = (elemento: PlanEstudioI) => {
    setDefaultOptionsSelected((st) => ({
      ...st,
      default_selected_plan_estudio: elemento,
    }));
    setValues({ ...values, plan_estudio_id: elemento.id });
  };

  useEffect(() => {
    if (abmAction == "edit") loadEditForm();
    if (!estados) dispatch(fetchEstados());
    loadInstituciones();
  }, []);

  // Solo se permite una caja y debe haber un plan de estudio seleccionado
  const canAddCaja =
    cajaCurricularPciList.length === 0 && !!values.plan_estudio_id;

  // Solo se pueden agregar espacios si hay al menos una caja
  const canAddEspacio = cajaCurricularPciList.length > 0;

  // Se puede comparar si hay una caja con caja_curricular_id (base) y tiene espacios
  const canCompareBase =
    cajaCurricularPciList.length > 0 &&
    !!cajaCurricularPciList[0]?.caja_curricular_id &&
    cajaCurricularPciList[0]?.espaciosCurricularesPci?.length > 0;

  // --- Handler para toggle del comparador ---
  const handleToggleComparador = async () => {
    if (showComparador) {
      setShowComparador(false);
      return;
    }

    const cajaBaseId = cajaCurricularPciList[0]?.caja_curricular_id;
    if (!cajaBaseId) return;

    try {
      setLoadingComparador(true);
      setShowComparador(true);
      const res = await getEspaciosCurricularesByCajaCurricular({
        caja_curricular_id: cajaBaseId,
      });
      setEspaciosBaseComparador(res?.elementos ?? []);
    } catch (error) {
      console.error("Error al obtener espacios base para comparar:", error);
      showErrorToast("Error al cargar espacios base para la comparación.");
      setEspaciosBaseComparador([]);
    } finally {
      setLoadingComparador(false);
    }
  };

  // --- Mapeo de datos para el comparador ---
  const comparadorEspaciosBase: ComparadorEspacioBase[] = useMemo(
    () =>
      espaciosBaseComparador.map((esp) => ({
        id: esp.id,
        nombre: esp.nombre,
        anio: esp.anio,
        horasSemanales: esp.horas_semanales,
        cuatrimestre: esp.cuatrimestre ?? null,
        area: esp.area?.nombre,
        tipoEspacio: esp.tipo_espacio?.nombre,
      })),
    [espaciosBaseComparador]
  );

  const comparadorEspaciosPci: ComparadorEspacioPci[] = useMemo(
    () =>
      cajaCurricularPciList.flatMap((caja) =>
        caja.espaciosCurricularesPci.map((esp) => ({
          id: esp.id,
          espacioCurricularId: esp.espacio_curricular_id,
          nombre: esp.nombre,
          anio: (esp as any).anio,
          horasSemanales: esp.carga_horaria,
          cuatrimestre: esp.cuatrimestre ?? null,
          area: (esp as any).area_nombre ?? "",
          tipoEspacio: (esp as any).tipo_espacio_nombre ?? "",
        }))
      ),
    [cajaCurricularPciList]
  );

  // IDs de espacios base ya utilizados por los espacios PCI actuales
  const usedEspacioBaseIds: (number | string)[] = useMemo(
    () =>
      cajaCurricularPciList.flatMap((caja) =>
        caja.espaciosCurricularesPci
          .map((esp) => esp.espacio_curricular_id)
          .filter((id): id is number | string => id !== undefined && id !== "")
      ),
    [cajaCurricularPciList]
  );

  // Elemento que se está editando (para pasar al modal)
  const editingCajaElemento =
    editingCajaIndex !== null
      ? cajaCurricularPciList[editingCajaIndex]
      : undefined;

  const editingEspacioElemento =
    editingEspacioIndex !== null
      ? cajaCurricularPciList[editingEspacioIndex.cajaIndex]
          ?.espaciosCurricularesPci[editingEspacioIndex.espacioIndex]
      : undefined;

  const editingEspacioDefaultCajaIndex =
    editingEspacioIndex !== null
      ? String(editingEspacioIndex.cajaIndex)
      : undefined;

  return (
    <section>
      <h1 className="section-title">
        {abmAction == "edit" ? "Editar PCI" : "Nuevo PCI"}
      </h1>

      <h2 className="section-subtitle mt-2">
        Edita o crea un nuevo Proyecto Curricular Institucional.
      </h2>
      <Card>
        {isLoading.form && abmAction === "edit" ? (
          <div className="d-flex justify-content-center align-items-center mt-5 mb-5 p-5">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : (
        <form onSubmit={handleSubmit}>
          <Row className="mb-3">
            <Col lg={4} className="mb-3">
              <Input
                id="nombre"
                onChange={handleChange}
                values={values}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                variant="gray-container"
                title="Nombre"
                size="lg"
                required
              />
            </Col>

            <Col lg={4} className="mb-3">
              <Input
                id="anio_implementacion"
                type="number"
                onChange={handleChange}
                values={values}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                variant="gray-container"
                title="Año de implementación"
                size="lg"
                min={1900}
                max={2100}
                required
              />
            </Col>
            <Col sm={4} className="mb-3">
              <Select
                id="estado_id"
                title="Estado"
                size="lg"
                optionsFormatConfig={{ value: "id", label: "nombre" }}
                options={estados ?? []}
                isLoading={loadingAppData.estados}
                placeholder="Seleccionar"
                variant="gray-container"
                required
                values={values}
                errors={errors}
                touched={touched}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </Col>
          </Row>

          <Row className="mb-3">

            <Col sm={4} className="mb-3">
              <AsyncSelect
                onChange={handleChange}
                id="institucion_id"
                values={values}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                placeholder={"Buscar institución (nombre o CUE)"}
                size="lg"
                title="Institución"
                getter={(params) => getInstituciones({ limit: 50, ...params })}
                paramKey={"param"}
                variant="gray-container"
                arrElementsPath="instituciones"
                optionsFormatConfig={{
                  value: "id",
                  label: (el: any) => `${el.nombre} (CUE: ${el.codigo_cue})`,
                }}
                defaultSelectedOptions={
                  defaultOptionsSelected["default_selected_institucion"]
                }
                required
                noOptionsMessage={`No hay opciones disponibles para el término ingresado`}
                emptySelectMessage={`Ingresar nombre o CUE`}
              />
            </Col>
          </Row>

                <hr></hr>
      
                <Row className="">
                  <Col sm={8} className="mb-3">
                    <AsyncSelect
                      onChange={handleChange}
                      id="normativas_ids"
                      values={values}
                      errors={errors}
                      touched={touched}
                      onBlur={handleBlur}
                      placeholder={"Buscar nombre de la normativa"}
                      size="lg"
                      title="Normativas"
                      getter={(params) => {
                        const [key, value] = Object.entries(params)[0];
                        return getNormativas({ [key]: value, limit: 50 });
                      }}
                      paramKey={"nombre"}
                      isMulti={true}
                      variant="gray-container"
                      arrElementsPath="elementos"
                      optionsFormatConfig={{
                        value: "id",
                        label: (el: NormativaI) => `${el.nombre}`,
                      }}
                      defaultSelectedOptions={
                        defaultOptionsSelected["default_selected_normativas"]
                      }
                      noOptionsMessage={`No hay opciones disponibles para el nombre ingresado`}
                      emptySelectMessage={`Ingresar un nombre`}
                    />
                  </Col>
                </Row>
                
          <hr></hr>

          <Row className="">
            <Col sm={8} className="mb-3">
              <AsyncSelect
                onChange={handleChange}
                id="plan_estudio_id"
                values={values}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                placeholder={"Buscar nombre de plan de estudio"}
                size="lg"
                title="Plan de estudio"
                getter={(params) => getPlanesEstudio({ limit: 50, ...params })}
                paramKey={"nombre"}
                variant="gray-container"
                arrElementsPath="elementos"
                optionsFormatConfig={{
                  value: "id",
                  label: (el: PlanEstudioI) => `${el.nombre}`,
                }}
                defaultSelectedOptions={
                  defaultOptionsSelected["default_selected_plan_estudio"]
                }
                required
                noOptionsMessage={`No hay opciones disponibles para el nombre ingresado`}
                emptySelectMessage={`Ingresar un nombre`}
              />
            </Col>
          </Row>


          <hr></hr>

          {/* Sub-entidades: Cajas Curriculares PCI */}
          <Row className="">
            <Col className="mb-3 d-flex align-items-center">
              <h5 className="mb-0">Cajas Curriculares PCI</h5>
            </Col>
            <Col className="d-flex justify-content-end mb-3">
              <Button
                onClick={() => handleCreateModal("cajaCurricularPci")}
                variant="secondary"
                size={"lg"}
                disabled={!canAddCaja}
                style={{
                  height: "50px",
                  borderRadius: "8px",
                  fontSize: "1rem",
                  ...(!canAddCaja && {
                    opacity: 0.5,
                    pointerEvents: "none" as const,
                    cursor: "not-allowed",
                    backgroundColor: "inherit",
                    borderColor: "#20465eff",
                    color: "#20465eff",
                  }),
                }}
              >
              Nueva Caja Curricular PCI
            </Button>
            </Col>
          </Row>
          {!values.plan_estudio_id && (
            <p className="text-muted small">
              Primero debe seleccionar un Plan de Estudio para poder agregar
              una Caja Curricular PCI.
            </p>
          )}

          {cajaCurricularPciList.length > 0 && (
            <Row className="mt-2">
              {cajaCurricularPciList.map((caja, cajaIndex) => (
                <Col sm={12} className="mb-2" key={cajaIndex}>
                  <div className="pill-card">
                    {`${caja.descripcion} (v${caja.version})`}
                    <div className="d-flex align-items-center">
                      <Button
                        variant="link"
                        onClick={() => handleOpenEditCaja(cajaIndex)}
                      >
                        <EditIcon
                          size={18}
                          color={LISTA_COLORES_ICONOS.DARK_GRAY}
                        />
                      </Button>
                      <Button
                        variant="link"
                        onClick={() => handleRemoveCajaCurricular(cajaIndex)}
                      >
                        <CloseIcon
                          size={30}
                          color={LISTA_COLORES_ICONOS.DARK_GRAY}
                        />
                      </Button>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          )}

          <hr></hr>

          {/* Sub-entidades: Espacios Curriculares PCI */}
          <Row className="">
            <Col className="mb-3 d-flex align-items-center">
              <h5 className="mb-0">Espacios Curriculares PCI</h5>
            </Col>
            <Col className="d-flex gap-2 justify-content-end mb-3">
              <Button
                onClick={handleToggleComparador}
                variant="secondary"
                size={"lg"}
                disabled={!canCompareBase}
                style={{
                  height: "50px",
                  borderRadius: "8px",
                  fontSize: "1rem",
                  ...(!canCompareBase && {
                    opacity: 0.5,
                    pointerEvents: "none" as const,
                    cursor: "not-allowed",
                    backgroundColor: "inherit",
                    borderColor: "#20465eff",
                    color: "#20465eff",
                  }),
                }}
              >
                {showComparador ? "Ocultar Comparación" : "Comparar Base"}
              </Button>
              <Button
                onClick={() => handleCreateModal("espacioCurricularPci")}
                variant="secondary"
                size={"lg"}
                disabled={!canAddEspacio}
                style={{
                  height: "50px",
                  borderRadius: "8px",
                  fontSize: "1rem",
                  ...(!canAddEspacio && {
                    opacity: 0.5,
                    pointerEvents: "none" as const,
                    cursor: "not-allowed",
                    backgroundColor: "inherit",
                    borderColor: "#20465eff",
                    color: "#20465eff",
                  }),
                }}
              >
                Nuevo Espacio Curricular PCI
              </Button>
            </Col>
          </Row>
          {!canAddEspacio && (
            <p className="text-muted small">
              Primero debe crear una Caja Curricular PCI para poder agregar
              espacios.
            </p>
          )}

          {!showComparador &&
            cajaCurricularPciList.some(
              (c) => c.espaciosCurricularesPci.length > 0,
            ) && (
            <Row className="mt-2">
              <Col sm={12}>
                <EspaciosTable
                  espacios={cajaCurricularPciList.flatMap((caja, cajaIndex) =>
                    caja.espaciosCurricularesPci.map((espacio, espacioIndex) => ({
                      id: espacio.id,
                      nombre: espacio.nombre,
                      anio: (espacio as any).anio ?? 0,
                      horasSemanales: espacio.carga_horaria,
                      cuatrimestre: espacio.cuatrimestre ?? null,
                      area: (espacio as any).area_nombre ?? "",
                      tipoEspacio: (espacio as any).tipo_espacio_nombre ?? "",
                      detalleModificacion: espacio.detalle_modificacion,
                      _cajaIndex: cajaIndex,
                      _espacioIndex: espacioIndex,
                    } as EspaciosTableRow & { _cajaIndex: number; _espacioIndex: number }))
                  )}
                  showDetalleModificacion
                  onEdit={(idx) => {
                    // Determinar cajaIndex y espacioIndex a partir del índice plano
                    let flatIdx = 0;
                    for (let ci = 0; ci < cajaCurricularPciList.length; ci++) {
                      const espacios = cajaCurricularPciList[ci].espaciosCurricularesPci;
                      for (let ei = 0; ei < espacios.length; ei++) {
                        if (flatIdx === idx) {
                          handleOpenEditEspacio(ci, ei);
                          return;
                        }
                        flatIdx++;
                      }
                    }
                  }}
                  onDelete={(idx) => {
                    let flatIdx = 0;
                    for (let ci = 0; ci < cajaCurricularPciList.length; ci++) {
                      const espacios = cajaCurricularPciList[ci].espaciosCurricularesPci;
                      for (let ei = 0; ei < espacios.length; ei++) {
                        if (flatIdx === idx) {
                          handleRequestDeleteEspacio(ci, ei);
                          return;
                        }
                        flatIdx++;
                      }
                    }
                  }}
                  emptyMessage="No hay espacios curriculares agregados."
                  maxHeight="400px"
                />
              </Col>
            </Row>
          )}

          {/* Comparador Base vs PCI */}
          <Collapse in={showComparador}>
            <div className="mt-3 mb-3">
              <h6 className="fw-bold mb-2">Comparación: Caja Base vs Caja PCI</h6>
              {loadingComparador ? (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Cargando comparación...
                </div>
              ) : (
                <EspaciosComparador
                  espaciosBase={comparadorEspaciosBase}
                  espaciosPci={comparadorEspaciosPci}
                />
              )}
            </div>
          </Collapse>

          <hr></hr>

          <Row>
            <Col>
              <div className="w-100 d-flex column-gap-3 justify-content-end">
                <Button variant="secondary" size={"lg"} onClick={handleReturn}>
                  Cancelar
                </Button>
                <Button variant="primary" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Confirmando..." : "Confirmar"}
                </Button>
              </div>
            </Col>
          </Row>
        </form>
        )}
      </Card>

      <CajaCurricularPciABM
        showModal={showModalABM.cajaCurricularPci}
        actionType={modalsActionType}
        onClose={() => handleCloseModal("cajaCurricularPci")}
        planEstudioId={values.plan_estudio_id}
        elemento={editingCajaElemento}
        onSuccess={handleCajaSuccess}
      />

      <EspacioCurricularPciABM
        showModal={showModalABM.espacioCurricularPci}
        actionType={modalsActionType}
        onClose={() => handleCloseModal("espacioCurricularPci")}
        onSuccess={handleEspacioSuccess}
        cajaCurricularPciList={cajaCurricularPciList}
        elemento={editingEspacioElemento}
        defaultCajaIndex={editingEspacioDefaultCajaIndex}
        usedEspacioBaseIds={usedEspacioBaseIds}
      />
      {/* Modal de confirmación para eliminar espacio PCI */}
      <Modal show={pendingDeleteEspacio !== null} onHide={handleCancelDeleteEspacio} centered>
        <Modal.Header>
          <Modal.Title>Eliminar Espacio Curricular PCI</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          ¿Estás seguro de que querés eliminar este espacio curricular PCI?
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="mx-3"
            variant="secondary"
            size="lg"
            onClick={handleCancelDeleteEspacio}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            size="lg"
            onClick={handleConfirmDeleteEspacio}
          >
            Confirmar
          </Button>
        </Modal.Footer>
      </Modal>
    </section>
  );
};
